import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import Copy from "../../../assets/images/icons/new-copy.svg";
import { ICONS, ACTION_DISPLAY_TYPES } from "../../../constants/enums";
import { GetCardShowDetailsResponse } from "../../../types/api/card";
import moment from "moment";

import Alert from "../../Common/Alerts/Alert";
import Action from "../../Common/Action";
import i18n from "../../../i18n/config";

const CardDetailsBody = ({ data, setOpenCardDetailsModal, isLocked }: { data: GetCardShowDetailsResponse, setOpenCardDetailsModal: Dispatch<SetStateAction<boolean>>, isLocked: boolean }) => {
  const [timeLeft, setTimeLeft] = useState(data.windowLifetimeSeconds);
  const [countdownDisplay, setCountdownDisplay] = useState('');

  useEffect(() => {
    const updateDisplay = (seconds: number) => {
      const duration = moment.duration(seconds, 'seconds');
      const minutes = duration.minutes();
      const secs = String(duration.seconds()).padStart(2, '0');
      setCountdownDisplay(`${minutes}:${secs}`);
    };

    updateDisplay(timeLeft); // initial display

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setOpenCardDetailsModal(false); // close modal when time is up
          return 0;
        }

        const newTime = prev - 1;
        updateDisplay(newTime);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval); // cleanup on unmount
  }, [data.windowLifetimeSeconds]);

  const ccChunks = data.cardNumber.match(/.{1,4}/g) || [];

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  }

  return (
    <>
      {isLocked &&
        <Alert
          icon={ICONS.LOCK_BLUE}
          alertClasses="flex-row align-items-start alert-info spacing-bottom-tiny"
        >
          <h4 className="font-size-default bold navy-blue spacing-left-tiny spacing-bottom-xs">{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CARD_LOCKED_ALERT.TITLE")}</h4>
          <p className="light-gray-600 spacing-bottom-none spacing-left-tiny">{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CARD_LOCKED_ALERT.MESSAGE")}</p>
        </Alert>
      }

      <Alert
        icon={ICONS.INFO}
        alertClasses="flex-row align-items-start alert-info spacing-bottom-tiny"
      >
        <h4 className="font-size-default bold navy-blue spacing-left-tiny spacing-bottom-xs">
          {i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.GENERAL_ALERT.TITLE")}
        </h4>
        <p className="light-gray-600 spacing-bottom-none spacing-left-tiny">
          {i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.GENERAL_ALERT.MESSAGE")}
        </p>
      </Alert>

      <div className="border-all border--beluga-white border-radius text-center padding-top-small padding-bottom-small padding-left-small padding-right-small">
        <p className="marengo-grey spacing-bottom-none">
          {i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CLOSING_MESSAGE")} {countdownDisplay}
        </p>
      </div>

      <article>
        <h4 className="gray-1000 bold">{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.DETAILS_HEADING")}</h4>

        <div className="group spacing-bottom-small">
          <p>{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CARD_NUMBER")}</p>
          <div className="flex-row align-items-center spacing-bottom-small gap-8">
            <p>
              {ccChunks.map((chunk: string, index: number) => (
                <span key={Number(chunk)} className={`${index < ccChunks.length - 1 ? 'spacing-right-tiny' : ''}`}>
                  {chunk}
                </span>
              ))}
            </p>

            <button className="no-styling mb-8" onClick={() => copyToClipboard(data.cardNumber)}>
              <img src={Copy} alt="copy" />
            </button>
          </div>
        </div>

        <div className="flex-row align-items-center spacing-bottom-small">
          <div className="group spacing-right-large">
            <p>{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.EXP_DATE")}</p>
            <p>{data.expirationDate}</p>
          </div>

          <div className="group">
            <p>{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CVV")}</p>
            <div className="flex-row align-items-center gap-8">
              <p>
                {data.cvv}
              </p>
              <button className="no-styling mb-8" onClick={() => copyToClipboard(data.cvv)}>
                <img src={Copy} alt="copy" />
              </button>
            </div>
          </div>

        </div>

        <div className="group">
          <p>{i18n.t("PAGES.MY_CARD.INDEX.CARD_DETAILS_MODAL.CARDHOLDERS_NAME")}</p>
          <p>{data.cardHolderName}</p>
        </div>

        <div className="flex-row justify-content-center align-items-center">
          <Action
            displayType={ACTION_DISPLAY_TYPES.TEXT}
            title={i18n.t("COMMON.CLOSE")}
            onClick={() => setOpenCardDetailsModal(false)}
            className="bold mt-16"
          />
        </div>
      </article>
    </>
  );
}

export default CardDetailsBody;